// DEV-2684 — mevcut büronun yeni yetki sistemine düğmeyle geçişi: ekranın saf mantığı.
// Geçiş bitince bu klasör (src/components/yetki-gecisi/) tek seferde silinir; dışarıdan bağı yalnız
// OfisTercihlerView'deki düğme, router'daki tek rota ve src/views/ofispro/YetkiGecisiView.vue'dur.
import axios, {AxiosAdapter} from "axios";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {KADEME_SECENEKLERI, KADEME_SIRA, YetkiKademe} from "@/enum/YetkiModulleri";
import {kullaniciYonetici, yeniYetkiModu} from "@/plugins/yetkiKontrol";

// Kullanıcı İzinleri rotası (router/index.ts) ve ondan türeyen geçiş sayfası.
export const KULLANICI_IZINLERI_YOLU = ProgramPaths.ofispro + "/kullanici-izinleri";
export const YETKI_GECISI_YOLU = KULLANICI_IZINLERI_YOLU + "/yetki-gecisi";

// /ben `yetki.gecis_dugmesi` yalnız eski moddaki yöneticide anlamlı; sunucu eski modda yöneticiye (geri
// alınmış büro dahil) true der. Alan gelmemişse (bayat oturum, eski API) düğme yok.
function eskiModYonetici(user: any): boolean {
  return !!(user && user.yetki) && !yeniYetkiModu(user) && kullaniciYonetici(user);
}

export function gecisDugmesiAcik(user: any): boolean {
  return eskiModYonetici(user) && user.yetki.gecis_dugmesi === true;
}

export type Uyum = "ayni" | "dar" | "genis" | "farkli";

export interface Hucre {
  kademe: YetkiKademe | string;
  ekle: boolean;
  degistir: boolean;
  sil: boolean;
}

export type HucreHaritasi = { [modul: string]: Hucre };

const BAYRAKLAR: Array<{ alan: "ekle" | "degistir" | "sil"; etiket: string }> = [
  {alan: "ekle", etiket: "Ekle"},
  {alan: "degistir", etiket: "Değiştir"},
  {alan: "sil", etiket: "Sil"},
];

// Sunucudaki BuroGocDegerlendirici::erisim ile aynı tanım: kademe basamakları + (yalnız Düzenle'de)
// açık bayraklar. Uyum sözlüğü ekranla sunucu arasında tek olsun diye aynı küme kullanılır.
function erisimKumesi(h: Hucre): string[] {
  const sira = KADEME_SIRA[h.kademe as YetkiKademe] ?? 0;
  const kume = KADEME_SECENEKLERI.filter((s) => KADEME_SIRA[s.value] > 0 && KADEME_SIRA[s.value] <= sira)
    .map((s) => s.value as string);
  if (h.kademe === YetkiKademe.DUZENLE) {
    BAYRAKLAR.forEach((b) => {
      if (h[b.alan]) {
        kume.push(b.alan);
      }
    });
  }
  return kume;
}

export function hucreAyni(a: Hucre, b: Hucre): boolean {
  const x = erisimKumesi(a);
  const y = erisimKumesi(b);
  return x.length === y.length && x.every((e) => y.indexOf(e) !== -1);
}

/** Seçilen hücre bugünkü karşılığına göre aynı / dar / geniş / farklı (biri dar biri geniş). */
export function uyumHesapla(bugun: Hucre, secili: Hucre): Uyum {
  const a = erisimKumesi(bugun);
  const b = erisimKumesi(secili);
  const bA = b.every((e) => a.indexOf(e) !== -1);
  const aB = a.every((e) => b.indexOf(e) !== -1);
  if (aB && bA) {
    return "ayni";
  }
  if (bA) {
    return "dar";
  }
  return aB ? "genis" : "farkli";
}

export function uyumOzeti(secim: HucreHaritasi, bugun: HucreHaritasi): { [u in Uyum]: number } {
  const ozet = {ayni: 0, dar: 0, genis: 0, farkli: 0};
  Object.keys(secim).forEach((modul) => {
    if (bugun[modul]) {
      ozet[uyumHesapla(bugun[modul], secim[modul])]++;
    }
  });
  return ozet;
}

/** "Görüntüle", "Düzenle (Ekle, Sil)" — bayraklar yalnız Düzenle'de anlamlıdır. */
export function hucreMetni(h: Hucre): string {
  const secenek = KADEME_SECENEKLERI.find((s) => s.value === h.kademe);
  const ad = secenek ? secenek.label : String(h.kademe);
  if (h.kademe !== YetkiKademe.DUZENLE) {
    return ad;
  }
  const acik = BAYRAKLAR.filter((b) => h[b.alan]).map((b) => b.etiket);
  return acik.length ? `${ad} (${acik.join(", ")})` : ad;
}

/** Gönderilecek hücre: Düzenle dışında bayraklar false (sunucu aksi hâlde 422 verir). */
function gonderimHucresi(h: Hucre): Hucre {
  const duzenle = h.kademe === YetkiKademe.DUZENLE;
  return {kademe: h.kademe, ekle: duzenle && !!h.ekle, degistir: duzenle && !!h.degistir, sil: duzenle && !!h.sil};
}

/**
 * POST gövdesinin `roller` kısmı: yalnız önerilenden farklı hücreler, rol kimliğiyle. Sunucu
 * gönderilmeyen rol/modülü önerilenle tamamlar; değişmeyen rol gövdeye hiç girmez.
 */
export function gonderilecekRoller(
  secimler: { [rolId: string]: HucreHaritasi },
  onerilenler: { [rolId: string]: HucreHaritasi }
): { [rolId: string]: HucreHaritasi } {
  const sonuc: { [rolId: string]: HucreHaritasi } = {};
  Object.keys(secimler).forEach((rolId) => {
    const onerilen = onerilenler[rolId] || {};
    Object.keys(secimler[rolId]).forEach((modul) => {
      const secili = secimler[rolId][modul];
      if (onerilen[modul] && hucreAyni(onerilen[modul], secili)) {
        return;
      }
      if (!sonuc[rolId]) {
        sonuc[rolId] = {};
      }
      sonuc[rolId][modul] = gonderimHucresi(secili);
    });
  });
  return sonuc;
}

export interface GecisHatalari {
  genel: string[];
  rol: { [rolId: string]: string[] };
  hucre: { [rolId: string]: { [modul: string]: string[] } };
}

/** 422 `errors` anahtarlarını ayırır: roller.{id}.{modul}(.alan) hücre, roller.{id} rol, diğeri genel. */
export function gecisHatalari(errors: { [anahtar: string]: string[] | string } | null | undefined): GecisHatalari {
  const sonuc: GecisHatalari = {genel: [], rol: {}, hucre: {}};
  Object.keys(errors || {}).forEach((anahtar) => {
    const ham = (errors as any)[anahtar];
    const mesajlar: string[] = Array.isArray(ham) ? ham.map(String) : [String(ham)];
    const parca = anahtar.split(".");
    if (parca[0] !== "roller" || parca.length < 2) {
      sonuc.genel.push(...mesajlar);
      return;
    }
    const rolId = parca[1];
    if (parca.length === 2) {
      sonuc.rol[rolId] = (sonuc.rol[rolId] || []).concat(mesajlar);
      return;
    }
    const modul = parca[2];
    if (!sonuc.hucre[rolId]) {
      sonuc.hucre[rolId] = {};
    }
    sonuc.hucre[rolId][modul] = (sonuc.hucre[rolId][modul] || []).concat(mesajlar);
  });
  return sonuc;
}

/**
 * Yalnız bu isteğin verilen kodlardaki hatasını genel uyarıya "gösterildi" diye işaretler; mesajı ekran kendisi
 * gösterir. StatusCodeHelper bu işarete master'da da bakar (`__uyariGosterildi`); uygulama geneli bayrak
 * (dontShow*Alert) ve öbür istekler etkilenmez, diğer kodlar genel akışta kalır.
 */
export function kendiUyarisiylaAdaptor(temel: AxiosAdapter | undefined, kodlar: number[]): AxiosAdapter {
  const asil = (temel || axios.defaults.adapter) as AxiosAdapter;
  return (config) => asil(config).catch((hata: any) => {
    if (hata && hata.response && kodlar.indexOf(hata.response.status) !== -1) {
      hata.__uyariGosterildi = true;
    }
    return Promise.reject(hata);
  });
}
